import type { HotEditSelection } from '../stores/hotEdit';

/**
 * What Ideoz is told about the element the founder is asking about (Prototype Editor UX plan,
 * step 5).
 *
 * The founder writes what they want in their own words - "make this stand out more" - and the
 * editor says quietly which element they meant: its file and line, the component that draws it,
 * its classes, its words, and whether it is one row of a list. None of this appears in the chat:
 * pasting `src/pages/Alerts.tsx, line 57` into the composer made the founder edit a sentence
 * about code before they could ask for anything.
 *
 * Pure: the shape and its prose, with no store and no DOM.
 */
export type ElementContext = {
  /** What the founder sees on the chip: "Alert card, 3 of 5". */
  label: string;
  filePath?: string;
  lineNumber?: number;
  componentName?: string;
  tagName: string;
  className?: string;
  text?: string;
  /** What Chef marked this element as, when the prototype carries marks. */
  editMark?: string;
  list?: { count: number; index: number; key?: string };
  /** The route the preview was showing, so the model looks at the right page. */
  pathname?: string;
};

const MAX_TEXT = 160;
const MAX_CLASSES = 400;

export function elementContextOf(
  selection: HotEditSelection,
  opts: { label: string; pathname?: string },
): ElementContext {
  const text = selection.textContent.replace(/\s+/g, ' ').trim();
  const row = selection.listRow;
  return {
    label: opts.label,
    ...(selection.source?.filePath ? { filePath: selection.source.filePath } : {}),
    ...(selection.source?.lineNumber ? { lineNumber: selection.source.lineNumber } : {}),
    ...(selection.ownerComponentName ? { componentName: selection.ownerComponentName } : {}),
    tagName: selection.tagName.toLowerCase(),
    ...(selection.className ? { className: clip(selection.className, MAX_CLASSES) } : {}),
    ...(text ? { text: clip(text, MAX_TEXT) } : {}),
    ...(selection.editMark ? { editMark: selection.editMark } : {}),
    ...(row && row.count > 1
      ? { list: { count: row.count, index: row.index, ...(row.key ? { key: row.key } : {}) } }
      : {}),
    ...(routeOf(opts.pathname) ? { pathname: routeOf(opts.pathname)! } : {}),
  };
}

/**
 * The page the founder is on, when there is one worth naming. The light preview runs from a blob
 * document, so its location is `/3f2c...-…` - the browser's name for the file, not a route, and
 * nothing the model should be told to look at.
 */
function routeOf(pathname?: string): string | null {
  if (!pathname || !pathname.startsWith('/')) return null;
  const rest = pathname.slice(1);
  const looksLikeABlob = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rest);
  return looksLikeABlob ? null : pathname;
}

/**
 * The same thing as the model reads it. Plain lines rather than JSON: it sits inside the
 * founder's own message, where a wall of braces would compete with what they wrote.
 */
export function describeElementContext(context: ElementContext): string {
  const lines: string[] = [`The founder is asking about: ${context.label}`];
  if (context.filePath) {
    lines.push(`Written in: ${context.filePath}${context.lineNumber ? `, line ${context.lineNumber}` : ''}`);
  }
  if (context.componentName) lines.push(`Component: ${context.componentName}`);
  lines.push(`Element: <${context.tagName}>${context.editMark ? ` (data-edit="${context.editMark}")` : ''}`);
  if (context.className) lines.push(`Classes: ${context.className}`);
  if (context.text) lines.push(`Its words: "${context.text}"`);
  if (context.list) {
    lines.push(
      `One of ${context.list.count} rows of a list (row ${context.list.index}${
        context.list.key ? `, key ${context.list.key}` : ''
      }) - a change to its design changes every row unless the founder says otherwise.`,
    );
  }
  if (context.pathname) lines.push(`Page: ${context.pathname}`);
  return lines.join('\n');
}

function clip(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 3)}...`;
}
